import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectBody } from '../../utils/apiError';
import { BACKGROUND_READ_TIMEOUT_MS } from '../../utils/readTimeout';
import { logout } from './authSlice';
import * as whatsappScope from './whatsappScope';

const WHATSAPP_URL = '/api/whatsapp';
// The super-admin portal links the PLATFORM's own handset, which is not a
// tenant and so cannot use the routes above: they resolve the company from the
// caller's token and refuse a super admin, who has none. Same three endpoints,
// same response shape, different mount — so the session thunks and
// WhatsAppSessionCard take a scope and everything else is shared.
//
// 'tenant' is the default on every thunk, so the tenant call sites are byte
// for byte the calls they were.
const PLATFORM_WHATSAPP_URL = '/api/super-admin/whatsapp';
const sessionUrl = (scope) => (scope === 'platform' ? PLATFORM_WHATSAPP_URL : WHATSAPP_URL);

const errOf = (error) => error.response?.data?.error || error.message;

// Campaign actions (send/pause/cancel) can fail with a specific machine
// code (e.g. session_not_connected) the UI needs to show translated copy
// for, distinct from the generic translated failure — errOf alone drops it.
const errWithCode = (error) => ({ error: errOf(error), code: error.response?.data?.code });

export const fetchWaSession = createAsyncThunk(
  'whatsapp/fetchSession',
  async (scope, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${sessionUrl(scope)}/session`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const fetchWaConnectionStatus = createAsyncThunk(
  'whatsapp/fetchConnectionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${WHATSAPP_URL}/connection-status`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const startWaPairing = createAsyncThunk(
  'whatsapp/startPairing',
  async (scope, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${sessionUrl(scope)}/session`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const disconnectWa = createAsyncThunk(
  'whatsapp/disconnect',
  async (scope, { rejectWithValue }) => {
    try {
      const res = await axios.delete(`${sessionUrl(scope)}/session`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const fetchWaChats = createAsyncThunk(
  'whatsapp/fetchChats',
  async (params, { rejectWithValue, signal }) => {
    // `background` is a UI-only flag (read from action.meta.arg by the pending
    // reducer below to skip the loading toggle) — strip it before it leaks
    // into the querystring. Client scope and sequence tracking are also stripped.
    const { background, scopeKey, companyId, seq, ...queryParams } = params || {};
    try {
      const res = await axios.get(`${WHATSAPP_URL}/chats`, {
        params: queryParams,
        // `.abort()` on the dispatch handle has always rejected the ACTION;
        // without this the underlying GET stayed on the wire, so an abandoned
        // search term or a switched chat still cost a full request. Handing the
        // thunk's signal to axios makes cancellation reach the transport.
        signal,
        // Bounded read (B02) for the 4s inbox poll only. The poll keeps exactly
        // one slot open, so a socket that hangs without an OS/proxy reset would
        // otherwise stop the inbox refreshing for the life of the page. The
        // foreground list read is untouched: it is user-initiated and owns its
        // own spinner.
        ...(background ? { timeout: BACKGROUND_READ_TIMEOUT_MS } : {}),
      });
      if (res.data && (res.data.scopeRev !== undefined || res.data.full !== undefined)) {
        return {
          chats: res.data.data ?? [],
          scopeRev: res.data.scopeRev,
          full: Boolean(res.data.full),
          seq,
          scopeKey,
          companyId,
          since: queryParams.since,
        };
      }
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// Lightweight unread total for the sidebar badge (A06 / PERF-API-01 P06).
//
// Reads the authorized aggregate GET /api/whatsapp/unread-count, which sums
// EVERY chat of the caller's company behind the same auth/feature/permission
// gates as /chats. The old client-side sum over /chats?limit=1000 could only
// ever see the 50 rows that endpoint returns, so unread conversations older
// than the newest 50 were missing from the badge; it also pulled a full chat
// page to produce one integer. /chats is unchanged for every other caller.
export const fetchWaUnreadTotal = createAsyncThunk(
  'whatsapp/fetchUnreadTotal',
  async (_, { rejectWithValue }) => {
    try {
      // Bounded read (B02): this thunk is single-flight by `condition`, so a
      // response that never arrives would block every later badge read for the
      // life of the store. A timeout rejects instead, the lock is released and
      // the next visible tick tries again — the last known count stays on
      // screen meanwhile.
      const res = await axios.get(`${WHATSAPP_URL}/unread-count`, {
        timeout: BACKGROUND_READ_TIMEOUT_MS,
      });
      const body = res.data?.data ?? res.data ?? {};
      const count = Number(body.unreadCount);
      // A malformed/absent total shows no badge rather than "NaN".
      return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  },
  {
    // One badge read in flight at a time. The shell timer, a resync on return
    // and a mount can coincide; without this they become three identical reads
    // whose answers race each other into the same integer.
    condition: (_arg, { getState }) => !getState().whatsapp?.unreadTotalLoading,
  }
);

export const fetchWaMessages = createAsyncThunk(
  'whatsapp/fetchMessages',
  async ({ chatId, before, beforeId, after, afterId, background }, { rejectWithValue, signal }) => {
    try {
      // `background` is a UI-only flag (see the pending reducer) and never a
      // query param — history pagination still sends before/beforeId exactly as
      // it did, and the poll still sends after/afterId.
      const res = await axios.get(`${WHATSAPP_URL}/chats/${chatId}/messages`, {
        params: { before, beforeId, after, afterId },
        // Same two reasons as fetchWaChats: a switched conversation must
        // withdraw its read from the transport, not just stop listening to it,
        // and the poll's read is bounded so a hang cannot own the single slot.
        signal,
        ...(background ? { timeout: BACKGROUND_READ_TIMEOUT_MS } : {}),
      });
      const data = res.data?.data ?? res.data;
      const messages = Array.isArray(data) ? data : data?.messages || [];
      // Cloud chats only: the 24-hour window rides beside `data`, never in it.
      const withinWindow =
        typeof res.data?.withinWindow === 'boolean' ? res.data.withinWindow : undefined;
      return { chatId, messages, withinWindow };
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const sendWaMessage = createAsyncThunk(
  'whatsapp/sendMessage',
  async ({ chatId, text, file, tempId }, { rejectWithValue }) => {
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        if (text) {
          formData.append('text', text);
        }
        res = await axios.post(`${WHATSAPP_URL}/chats/${chatId}/messages`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        res = await axios.post(`${WHATSAPP_URL}/chats/${chatId}/messages`, { text });
      }
      return { chatId, message: res.data?.data ?? res.data, tempId };
    } catch (error) {
      return rejectWithValue({
        error: errOf(error),
        code: error.response?.data?.code,
        status: error.response?.status,
        chatId,
        tempId,
      });
    }
  }
);

// 1-to-1 compose (item 5): resolve/create the chat for a customer, then the
// caller (WhatsAppInbox) opens it directly — same row shape as GET /chats.
// T11: also supports starting a chat with a brand-new number — the caller
// passes either { customer_id } or { name, phone }; pass the body through
// as-is rather than hardcoding its shape.
export const startWaChat = createAsyncThunk(
  'whatsapp/startChat',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${WHATSAPP_URL}/chats/start`, payload);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(errWithCode(error));
    }
  }
);

export const markWaChatRead = createAsyncThunk(
  'whatsapp/markRead',
  async (chatId, { rejectWithValue }) => {
    try {
      await axios.post(`${WHATSAPP_URL}/chats/${chatId}/read`);
      return chatId;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const linkWaCustomer = createAsyncThunk(
  'whatsapp/linkCustomer',
  async ({ chatId, customerId }, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${WHATSAPP_URL}/chats/${chatId}/link-customer`, {
        customer_id: customerId,
      });
      return { chatId, chat: res.data?.data ?? res.data };
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

// T11: prefer customer_id (handles duplicate-phone customers correctly on
// the backend) over phone matching. phone is kept as a fallback param for
// chats that aren't linked to a customer yet.
export const fetchWaActivity = createAsyncThunk(
  'whatsapp/fetchActivity',
  async ({ phone, customer_id, chatId } = {}, { rejectWithValue, signal }) => {
    try {
      // Poller-owned reference read (B02): bounded so a hung socket cannot
      // leave the activity panel spinning until the chat is closed, and
      // cancellable at the transport — the poller already calls `.abort()` on
      // this handle, which until now only stopped the store from listening.
      const res = await axios.get(`${WHATSAPP_URL}/activity`, {
        params: { phone, customer_id, ...(chatId ? { chatId } : {}) },
        signal,
        timeout: BACKGROUND_READ_TIMEOUT_MS,
      });
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const fetchWaCampaigns = createAsyncThunk(
  'whatsapp/fetchCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${WHATSAPP_URL}/campaigns`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const fetchWaCampaign = createAsyncThunk(
  'whatsapp/fetchCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${WHATSAPP_URL}/campaigns/${id}`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

export const createWaCampaign = createAsyncThunk(
  'whatsapp/createCampaign',
  async ({ name, body, customer_ids, extra_contacts, image, line }, { rejectWithValue }) => {
    try {
      let res;
      // Only include extra_contacts when non-empty so the request shape (and
      // existing tests asserting it) is unchanged for campaigns with no
      // ad-hoc recipients.
      if (image) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('body', body);
        formData.append('customer_ids', JSON.stringify(customer_ids));
        if (extra_contacts?.length) {
          formData.append('extra_contacts', JSON.stringify(extra_contacts));
        }
        // Multi-number: the line the campaign sends from ('main' or a number id).
        if (line) formData.append('line', line);
        formData.append('image', image);
        res = await axios.post(`${WHATSAPP_URL}/campaigns`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const payload = { name, body, customer_ids };
        if (extra_contacts?.length) payload.extra_contacts = extra_contacts;
        if (line) payload.line = line;
        res = await axios.post(`${WHATSAPP_URL}/campaigns`, payload);
      }
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(errWithCode(error));
    }
  }
);

export const sendWaCampaign = createAsyncThunk(
  'whatsapp/sendCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${WHATSAPP_URL}/campaigns/${id}/send`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(errWithCode(error));
    }
  }
);

export const pauseWaCampaign = createAsyncThunk(
  'whatsapp/pauseCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${WHATSAPP_URL}/campaigns/${id}/pause`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(errWithCode(error));
    }
  }
);

export const cancelWaCampaign = createAsyncThunk(
  'whatsapp/cancelCampaign',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${WHATSAPP_URL}/campaigns/${id}/cancel`);
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(errWithCode(error));
    }
  }
);

// Item 9 — per-company toggle for WhatsApp-first transactional routing.
// Per .sdd/wa/t9-backend-report.md: GET /api/whatsapp/session already carries
// wa_route_transactional, so there's no separate GET — the toggle's current
// value is read straight off `session` (see WhatsAppSettings.js). Only the
// write side needs a thunk.
export const updateWaSettings = createAsyncThunk(
  'whatsapp/updateSettings',
  async ({ route_transactional }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${WHATSAPP_URL}/settings`, { route_transactional });
      return res.data?.data ?? res.data;
    } catch (error) {
      return rejectWithValue(rejectBody(error));
    }
  }
);

const initialState = {
  session: null,
  loading: false,
  error: null,
  chats: [],
  messagesByChat: {},
  activity: null,
  activeChatId: null,
  chatsLoading: false,
  messagesLoading: false,
  sendingLoading: false,
  startChatLoading: false,
  activityLoading: false,
  campaigns: [],
  campaignsLoading: false,
  campaignDetail: null,
  campaignDetailLoading: false,
  campaignActionLoading: false,
  unreadTotal: 0,
  unreadTotalLoading: false,
  unreadTotalRequestId: null,
  settingsSaving: false,
  withinWindowByChat: {},
  connectionStatus: null,
  connectionMode: null,
  scopeRev: null,
  scopeRevChanges: 0,
  scopeChatAnswers: 0,
  visibleLines: [],
  scopeCache: {},
  currentScopeKey: null,
  currentCompanyId: null,
  lastProcessedSeq: 0,
  lastProcessedSeqByScope: {},
  // Request ids in flight, and those a sign-out disowned (see the reducer below).
  liveRequests: {},
  disowned: {},
};

const whatsappSlice = createSlice({
  name: 'whatsapp',
  initialState,
  reducers: {
    setActiveChatId: (state, action) => {
      state.activeChatId = action.payload;
    },
    clearActivity: (state) => {
      state.activity = null;
    },
    setCampaignDetail: (state, action) => {
      state.campaignDetail = action.payload;
    },
    clearCampaignDetail: (state) => {
      state.campaignDetail = null;
    },
    setVisibleLines: (state, action) => {
      state.visibleLines = action.payload || [];
    },
    setScopeKey: (state, action) => {
      const { companyId, scopeKey } = action.payload || {};
      whatsappScope.switchScope(state, companyId, scopeKey);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWaConnectionStatus.fulfilled, (state, action) => {
        state.connectionStatus = action.payload;
        state.connectionMode = action.payload?.mode || 'qr';
      })
      .addCase(fetchWaConnectionStatus.rejected, (state) => {
        state.connectionMode = 'qr';
      })
      .addCase(fetchWaSession.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaSession.fulfilled, (state, action) => {
        state.session = action.payload;
        state.loading = false;
        // A logged-out (unlinked) session must never show cached conversations
        // — covers reopening the inbox after an unlink from another tab/device
        // (the mount-time fetchWaChats can land before this and refill).
        // Connected native lines keep the inbox visible even if legacy session is logged out.
        // A multi-number chat list (state.scopeRev set) is scoped by the server
        // per line, so the main line's logged-out session does not clear it.
        const hasConnectedLine = state.visibleLines?.some((l) => l.connected);
        if (
          !state.scopeRev &&
          action.payload?.status === 'logged_out' &&
          state.connectionMode !== 'cloud' &&
          !hasConnectedLine
        ) {
          state.chats = [];
          state.messagesByChat = {};
          state.activity = null;
          state.activeChatId = null;
          state.unreadTotal = 0;
          // Disowning an in-flight badge read must also release the
          // single-flight lock, exactly as logout.fulfilled does. The stale
          // answer is still ignored by the request-id guard, but that guard
          // returns before it can clear the flag — so leaving it set here
          // would block every later badge read for the life of the store.
          state.unreadTotalRequestId = null;
          state.unreadTotalLoading = false;
        }
      })
      .addCase(fetchWaSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(startWaPairing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startWaPairing.fulfilled, (state, action) => {
        state.session = action.payload;
        state.loading = false;
      })
      .addCase(startWaPairing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(disconnectWa.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disconnectWa.fulfilled, (state) => {
        // 'logged_out' mirrors what the backend writes on unlink — deliberate
        // logout, distinct from a transient 'disconnected' (phone offline).
        state.session = {
          status: 'logged_out',
          waNumber: null,
          qrDataUrl: null,
          lastConnectedAt: null,
        };
        state.loading = false;
        // Unlinking clears the inbox: cached conversations must not outlive
        // the link (owner requirement 2026-07-05). DB rows are untouched —
        // relinking restores history via a fresh fetch.
        if (state.connectionMode !== 'cloud') {
          state.chats = [];
          state.messagesByChat = {};
          state.activity = null;
          state.activeChatId = null;
          state.unreadTotal = 0;
          // Same as the logged-out session path: release the single-flight lock
          // along with ownership, or the badge never refreshes after a relink.
          state.unreadTotalRequestId = null;
          state.unreadTotalLoading = false;
        }
      })
      .addCase(disconnectWa.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      // fetchWaChats — background (since-poll) fetches must NOT pin the
      // list-pane loading state, or an empty inbox shows "loading" forever
      // instead of wa.noChatsFound once the initial load has settled.
      .addCase(fetchWaChats.pending, (state, action) => {
        if (!action.meta.arg?.background) {
          state.chatsLoading = true;
        }
      })
      .addCase(fetchWaChats.fulfilled, (state, action) => {
        state.chatsLoading = false;
        // Unlinked session: drop the payload — the mount-time chats fetch can
        // resolve AFTER the session fetch and would refill the cleared inbox.
        // An answer that carries scopeRev (multi-number) must never be discarded
        // by the legacy logged-out guard.
        const hasConnectedLine = state.visibleLines?.some((l) => l.connected);
        const carriesScopeRev = Boolean(action.payload?.scopeRev);
        if (
          !carriesScopeRev &&
          state.session?.status === 'logged_out' &&
          state.connectionMode !== 'cloud' &&
          !hasConnectedLine
        ) {
          return;
        }
        whatsappScope.applyChatsFulfilled(state, action);
      })
      .addCase(fetchWaChats.rejected, (state, action) => {
        // A superseded search term abandons its read (WhatsAppInbox.js, P09).
        // The request that replaced it owns `chatsLoading` from its own pending,
        // and an abandoned read is not an error the inbox should announce.
        if (action.meta.aborted) return;
        state.chatsLoading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(fetchWaUnreadTotal.pending, (state, action) => {
        state.unreadTotalLoading = true;
        state.unreadTotalRequestId = action.meta.requestId;
      })
      .addCase(fetchWaUnreadTotal.fulfilled, (state, action) => {
        // Obsolete-response guard: a read issued before a logout or a session
        // scope change is dropped, because the reducer that cleared the scope
        // also cleared the request id it was waiting for. Without this a slow
        // answer from the previous session can repaint the next user's badge.
        if (state.unreadTotalRequestId !== action.meta.requestId) return;
        state.unreadTotalLoading = false;
        state.unreadTotalRequestId = null;
        state.unreadTotal = action.payload;
      })
      // A failed background badge poll is silent: don't clobber the shared
      // error banner over a transient sidebar-count refresh. The lock is
      // released either way, or one failure would stop the badge for good.
      .addCase(fetchWaUnreadTotal.rejected, (state, action) => {
        if (state.unreadTotalRequestId !== action.meta.requestId) return;
        state.unreadTotalLoading = false;
        state.unreadTotalRequestId = null;
      })
      // Signing out forgets the whole inbox: chats, messages, transport mode and
      // badge. The slice is not remounted between sessions in this store, so
      // the next user (perhaps a QR-only company) would otherwise inherit them.
      // Every request still in flight is disowned; whatsappReducer drops its
      // answer when it lands.
      .addCase(logout.fulfilled, (state) => {
        const disowned = {};
        [...Object.keys(state.disowned || {}), ...Object.keys(state.liveRequests || {})].forEach(
          (id) => {
            disowned[id] = true;
          }
        );
        return { ...initialState, disowned };
      })
      // fetchWaMessages — same audit as fetchWaChats: the 4s `after` poll
      // must not re-pin messagesLoading over an already-settled (possibly
      // empty, e.g. a brand-new chat) message list.
      .addCase(fetchWaMessages.pending, (state, action) => {
        if (!action.meta.arg?.background) {
          state.messagesLoading = true;
        }
      })
      .addCase(fetchWaMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const { chatId, messages, withinWindow } = action.payload;
        // In multi-number mode: apply messages answer only if its chat is still in
        // the current list. A pending messages answer must not recreate messages
        // deleted by a full replacement.
        if (state.scopeRev && state.chats && !state.chats.some((c) => c.id === chatId)) {
          return;
        }
        if (typeof withinWindow === 'boolean') {
          if (!state.withinWindowByChat) state.withinWindowByChat = {};
          state.withinWindowByChat[chatId] = withinWindow;
          const chat = state.chats?.find((c) => c.id === chatId);
          if (chat) chat.withinWindow = withinWindow;
        }
        // Unlinked session: same rule as fetchWaChats. A read issued before the
        // unlink can land after it and would refill a thread the operator was
        // just told is gone.
        const hasConnectedLine = state.visibleLines?.some((l) => l.connected);
        if (
          !state.scopeRev &&
          state.session?.status === 'logged_out' &&
          state.connectionMode !== 'cloud' &&
          !hasConnectedLine
        ) {
          return;
        }
        const existing = state.messagesByChat[chatId] || [];
        const msgMap = new Map();
        existing.forEach((m) => msgMap.set(m.id, m));
        messages.forEach((m) => msgMap.set(m.id, m));
        state.messagesByChat[chatId] = Array.from(msgMap.values()).sort(
          (a, b) => new Date(a.occurred_at) - new Date(b.occurred_at)
        );
      })
      .addCase(fetchWaMessages.rejected, (state, action) => {
        // An abandoned read — the conversation changed, the page unmounted, or
        // the poller cancelled a run that outlived its deadline. It is not an
        // error the operator did anything about, and it must not clear a
        // spinner that now belongs to the read which replaced it. Same rule as
        // fetchWaChats/fetchWaActivity.
        if (action.meta.aborted) return;
        state.messagesLoading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      // sendWaMessage
      .addCase(sendWaMessage.pending, (state, action) => {
        state.sendingLoading = true;
        const { chatId, text, file, tempId } = action.meta.arg;
        if (!state.messagesByChat[chatId]) {
          state.messagesByChat[chatId] = [];
        }
        // Real enum value (see WhatsappMessage model: text|image|document|other), not
        // the placeholder-only 'media' — must agree with the server object this row
        // gets replaced by on fulfilled, or the inbox's media-render check disagrees
        // between the optimistic and settled states.
        const optimisticType = file
          ? file.type.startsWith('image/')
            ? 'image'
            : 'document'
          : 'text';
        const optimisticMsg = {
          id: tempId,
          chat_id: chatId,
          direction: 'out',
          type: optimisticType,
          body: text || '',
          media_mime: file ? file.type : null,
          status: 'sending',
          occurred_at: new Date().toISOString(),
          sent_by: 'user',
        };
        state.messagesByChat[chatId].push(optimisticMsg);
      })
      .addCase(sendWaMessage.fulfilled, (state, action) => {
        state.sendingLoading = false;
        const { chatId, message, tempId } = action.payload;
        const list = state.messagesByChat[chatId] || [];
        state.messagesByChat[chatId] = list
          .map((m) => (m.id === tempId ? message : m))
          .filter((m, index, self) => self.findIndex((x) => x.id === m.id) === index)
          .sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));

        const chatIndex = state.chats.findIndex((c) => c.id === chatId);
        if (chatIndex > -1) {
          state.chats[chatIndex].last_message_preview = message.body;
          state.chats[chatIndex].last_message_at = message.occurred_at;
          state.chats.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
        }
      })
      .addCase(sendWaMessage.rejected, (state, action) => {
        state.sendingLoading = false;
        const { chatId, tempId, error } = action.payload || {};
        const realChatId = chatId || action.meta.arg.chatId;
        const realTempId = tempId || action.meta.arg.tempId;
        // The server closed the window: close the composer too, not just this send.
        if (action.payload?.code === 'WA_CLOUD_WINDOW_CLOSED') {
          if (!state.withinWindowByChat) state.withinWindowByChat = {};
          state.withinWindowByChat[realChatId] = false;
        }
        const list = state.messagesByChat[realChatId] || [];
        state.messagesByChat[realChatId] = list.map((m) => {
          if (m.id === realTempId) {
            return { ...m, status: 'failed', error: error || action.payload || 'Failed to send' };
          }
          return m;
        });
      })
      // markWaChatRead
      .addCase(markWaChatRead.fulfilled, (state, action) => {
        const chatId = action.payload;
        const chatIndex = state.chats.findIndex((c) => c.id === chatId);
        if (chatIndex > -1) {
          state.chats[chatIndex].unread_count = 0;
        }
      })
      // linkWaCustomer
      .addCase(linkWaCustomer.fulfilled, (state, action) => {
        const { chatId, chat } = action.payload;
        const chatIndex = state.chats.findIndex((c) => c.id === chatId);
        if (chatIndex > -1) {
          state.chats[chatIndex] = { ...state.chats[chatIndex], ...chat };
        }
      })
      // startWaChat (1-to-1 compose) — merge/insert the returned chat and
      // make it active so the caller opens it directly.
      .addCase(startWaChat.pending, (state) => {
        state.startChatLoading = true;
      })
      .addCase(startWaChat.fulfilled, (state, action) => {
        state.startChatLoading = false;
        const chat = action.payload;
        const idx = state.chats.findIndex((c) => c.id === chat.id);
        if (idx > -1) {
          state.chats[idx] = { ...state.chats[idx], ...chat };
        } else {
          state.chats.unshift(chat);
        }
        state.activeChatId = chat.id;
      })
      .addCase(startWaChat.rejected, (state) => {
        state.startChatLoading = false;
      })
      // WhatsApp settings (item 9 — transactional routing toggle). The
      // session already carries wa_route_transactional; on success the
      // caller (WhatsAppSettings.js) re-fetches the session for the
      // canonical value rather than this thunk guessing the response shape.
      .addCase(updateWaSettings.pending, (state) => {
        state.settingsSaving = true;
      })
      .addCase(updateWaSettings.fulfilled, (state) => {
        state.settingsSaving = false;
      })
      .addCase(updateWaSettings.rejected, (state) => {
        state.settingsSaving = false;
      })
      // Campaigns
      .addCase(fetchWaCampaigns.pending, (state) => {
        state.campaignsLoading = true;
      })
      .addCase(fetchWaCampaigns.fulfilled, (state, action) => {
        state.campaignsLoading = false;
        state.campaigns = action.payload;
      })
      .addCase(fetchWaCampaigns.rejected, (state, action) => {
        state.campaignsLoading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(fetchWaCampaign.pending, (state) => {
        state.campaignDetailLoading = true;
      })
      .addCase(fetchWaCampaign.fulfilled, (state, action) => {
        state.campaignDetailLoading = false;
        state.campaignDetail = action.payload;
      })
      .addCase(fetchWaCampaign.rejected, (state, action) => {
        state.campaignDetailLoading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(createWaCampaign.pending, (state) => {
        state.campaignActionLoading = true;
      })
      .addCase(createWaCampaign.fulfilled, (state, action) => {
        state.campaignActionLoading = false;
        state.campaignDetail = action.payload;
        const idx = state.campaigns.findIndex((c) => c.id === action.payload.id);
        if (idx > -1) {
          state.campaigns[idx] = action.payload;
        } else {
          state.campaigns.unshift(action.payload);
        }
      })
      .addCase(createWaCampaign.rejected, (state, action) => {
        state.campaignActionLoading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(sendWaCampaign.pending, (state) => {
        state.campaignActionLoading = true;
      })
      .addCase(sendWaCampaign.fulfilled, (state, action) => {
        state.campaignActionLoading = false;
        // Merge, don't replace: the bare campaign row has no progress/recipients,
        // so a wholesale replace blanked the progress bar until the next poll.
        state.campaignDetail = state.campaignDetail
          ? { ...state.campaignDetail, ...action.payload }
          : action.payload;
        const idx = state.campaigns.findIndex((c) => c.id === action.payload.id);
        if (idx > -1) state.campaigns[idx] = action.payload;
      })
      .addCase(sendWaCampaign.rejected, (state, action) => {
        state.campaignActionLoading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(pauseWaCampaign.pending, (state) => {
        state.campaignActionLoading = true;
      })
      .addCase(pauseWaCampaign.fulfilled, (state, action) => {
        state.campaignActionLoading = false;
        state.campaignDetail = state.campaignDetail
          ? { ...state.campaignDetail, ...action.payload }
          : action.payload;
        const idx = state.campaigns.findIndex((c) => c.id === action.payload.id);
        if (idx > -1) state.campaigns[idx] = action.payload;
      })
      .addCase(pauseWaCampaign.rejected, (state, action) => {
        state.campaignActionLoading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(cancelWaCampaign.pending, (state) => {
        state.campaignActionLoading = true;
      })
      .addCase(cancelWaCampaign.fulfilled, (state, action) => {
        state.campaignActionLoading = false;
        state.campaignDetail = state.campaignDetail
          ? { ...state.campaignDetail, ...action.payload }
          : action.payload;
        const idx = state.campaigns.findIndex((c) => c.id === action.payload.id);
        if (idx > -1) state.campaigns[idx] = action.payload;
      })
      .addCase(cancelWaCampaign.rejected, (state, action) => {
        state.campaignActionLoading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      // fetchWaActivity
      .addCase(fetchWaActivity.pending, (state) => {
        state.activityLoading = true;
      })
      .addCase(fetchWaActivity.fulfilled, (state, action) => {
        state.activityLoading = false;
        // In multi-number mode: apply activity answer only if its chat is still in
        // the current list and is still the active chat; otherwise drop it.
        if (state.scopeRev && action.meta.arg?.chatId) {
          const chatStillInList = (state.chats || []).some((c) => c.id === action.meta.arg.chatId);
          if (!chatStillInList || state.activeChatId !== action.meta.arg.chatId) {
            return;
          }
        }
        state.activity = action.payload;
      })
      .addCase(fetchWaActivity.rejected, (state, action) => {
        state.activityLoading = false;
        // An abandoned read — the panel unmounted, the chat changed, or the
        // poller cancelled a run that outlived its deadline — is not something
        // the user did wrong, so it never reaches the shared error banner.
        // Same rule as fetchWaChats above.
        if (action.meta.aborted) return;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      });
  },
});

export const {
  setActiveChatId,
  clearActivity,
  setCampaignDetail,
  clearCampaignDetail,
  setVisibleLines,
  setScopeKey,
} = whatsappSlice.actions;
const ownRequestId = (action) =>
  typeof action.type === 'string' && action.type.startsWith('whatsapp/')
    ? action.meta?.requestId
    : undefined;

// Sign-out fence. A read or send that was in flight when the user signed out
// answers for the previous session: its fulfilled/rejected action is dropped
// here, before any case reducer can paint it into the next user's inbox.
// Requests this store never saw start (a test dispatching an action directly)
// pass through untouched.
export default function whatsappReducer(state, action) {
  const id = ownRequestId(action);
  if (id && action.meta.requestStatus !== 'pending' && state?.disowned?.[id]) {
    const disowned = { ...state.disowned };
    delete disowned[id];
    return { ...state, disowned };
  }
  const next = whatsappSlice.reducer(state, action);
  if (!id) return next;
  const liveRequests = { ...next.liveRequests };
  if (action.meta.requestStatus === 'pending') liveRequests[id] = true;
  else delete liveRequests[id];
  return { ...next, liveRequests };
}
